import React from "react"
import { t } from "i18next"
import { twMerge } from "tailwind-merge"

import Modal from "components/Elements/Modal/Modal"
import Portal from "components/Elements/Portal"

import { fnuciBikeSelectedSelector } from "../slice/bikeFleetSelector"
import { StatusEnum, bikeFleetStatusListProps } from "../types/bikeFleetType"
import { updateBikeStatusThunk } from "../slice/BikeFleetThunk"

import { useTheme } from "hooks"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { CheckOutStateEnum } from "store/type"
import { getLabelFromStatusNumber } from "../utils"
import { useCurrentSearchParams } from "hooks/useCurrentSearchParams"
import { defaultBikeFleetQuery } from "../constant"
import { getFilteredBikeFleet } from "../slice/bikeFleetSlice"
import FormElement from "components/Form/FormElement"
import { isNotEmpty } from "utils"
import { error } from "console"

type BikeFleetStatusModalProps = {
	onClose: () => void
	selectedStatus: bikeFleetStatusListProps
}

export const BikeFleetStatusModal: React.FC<BikeFleetStatusModalProps> = ({
	selectedStatus,
	onClose,
}) => {
	const dispatch = useAppDispatch()
	const bikeSelected = useAppSelector(fnuciBikeSelectedSelector)
	const checkOutState = useAppSelector((state) => state.bikeFleet.checkOutState.updateBike)
	const [{ status }] = useCurrentSearchParams(defaultBikeFleetQuery)

	//Form Destroy
	const [destroyValue, setDestroyValue] = React.useState("")
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const {
		currentTheme: { id_api: operator },
	} = useTheme()

	//Boolean for know bike status selected is destroy
	const isDestroyStatus = React.useMemo(
		() => selectedStatus === StatusEnum.DESTRUCT,
		[selectedStatus]
	)
	//Boolean for know button confirm is ready click
	const isNotReadyForConfirm = React.useMemo(
		() =>
			checkOutState === CheckOutStateEnum.LOADING ||
			(isDestroyStatus &&
				destroyValue.toLowerCase() !==
					t("bikeFleet.newStatus.destroy", { ns: "bike" }).toLowerCase()),
		[isDestroyStatus, checkOutState, destroyValue]
	)

	const onConfirm = async () => {
		setShowLocalErrors(false)
		if (isNotEmpty(errors) && isDestroyStatus) return setShowLocalErrors(true)
		await dispatch(
			updateBikeStatusThunk({
				bikesToUpdate: bikeSelected.map((i) => ({
					operator: operator,
					bikeId: i.bike_id,
					rowVersion: i.row_version,
				})),
				bikeStatus: selectedStatus,
			})
		).unwrap()
		dispatch(getFilteredBikeFleet({ status, noChangeStatusUpdatedMessage: true }))
		onClose()
	}

	const handleInputChange = React.useCallback((event) => {
		setShowLocalErrors(false)
		setDestroyValue(event.target.value)
	}, [])

	const handleValidation = React.useCallback((name, errs) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	return (
		<Portal id="bikeFleetModal">
			<Modal
				authorizedClosingModal={checkOutState !== CheckOutStateEnum.LOADING}
				onClose={onClose}
				modalClassName={"py-12 px-24 flex flex-col max-w-screen max-h-screen relative"}
			>
				{checkOutState === CheckOutStateEnum.LOADING && (
					<div className="absolute w-full h-full top-0 left-0 bg-white/95 z-20 justify-center flex items-center flex-col p-8">
						<div className="font-bold h-[100px] text-xl">
							{t("bikeFleet.actions.updateLoading", { ns: "bike" })}
						</div>
						<div className="flex-1 justify-center items-center flex">
							<div className="c-spinner"></div>
						</div>
						<div className="text-lg h-[200px] flex items-center flex-col text-neutral-900">
							<div>{t("bikeFleet.actions.sentenceUploadFirst", { ns: "bike" })}</div>
							<div>{t("bikeFleet.actions.sentenceUploadSecond", { ns: "bike" })}</div>
							<div>{t("bikeFleet.actions.sentenceUploadThird", { ns: "bike" })}</div>
						</div>
					</div>
				)}
				<div>
					<h2 className="c-h2 text-primary text-xl font-bold text-center my-7">
						{t("bikeFleet.newStatus.confirmChange", { ns: "bike" })}
					</h2>
					<div className="text-left mt-8  text-neutral-900">
						{t("bikeFleet.newStatus.sentence", {
							ns: "bike",
							bikeNumber: bikeSelected?.length,
							plurial: bikeSelected?.length > 1 ? "s" : "",
						})}
					</div>
				</div>
				<div className="flex mt-8 flex-1">
					<div className="flex-1 relative">
						<div className="text-neutral-900">
							{t("bikeFleet.newStatus.bikeConcerned", { ns: "bike" })}
						</div>
						<div className="min-h-[100px] max-h-[200px]  overflow-auto pb-2 box-border mb-4 scroll-wrapper">
							{bikeSelected.map((bike) => {
								return (
									<div
										key={bike.bike_id}
										className={twMerge(
											"font-bold text-primary font-[Jost]  uppercase text-m pr-4  flex gap-2 items-center"
										)}
									>
										<strong className="flex-1">{bike.bike_id}</strong>
									</div>
								)
							})}
						</div>
					</div>
					<div className="flex-1  px-4 box-border">
						<div className="text-neutral-900">
							{t("bikeFleet.newStatus.newStatus", { ns: "bike" })}
						</div>
						<div className="font-bold text-lg font-[Jost]">
							{getLabelFromStatusNumber(selectedStatus)}
						</div>
					</div>
				</div>
				{isDestroyStatus && (
					<div className="flex flex-col gap-4 font-semibold text-neutral-900 pb-8">
						<div>{t("bikeFleet.newStatus.sentenceDestroy", { ns: "bike" })}</div>
						<div>{t("bikeFleet.newStatus.mentionDestroy", { ns: "bike" })}</div>

						<div data-cy="input-destroy">
							<FormElement
								classNameLabel={"hidden"}
								placeholder={t("bikeFleet.newStatus.destroyPlaceholder", {
									ns: "bike",
								})}
								name="destroy"
								type="text"
								customRules={[
									{
										key: "destroy",
										check: (value) =>
											isNotEmpty(value)
												? value.toLowerCase() ===
													t("bikeFleet.newStatus.destroy", {
														ns: "bike",
													}).toLowerCase()
												: true,
										message: t("bikeFleet.newStatus.destroyErrorForm", {
											ns: "bike",
										}),
									},
								]}
								value={destroyValue}
								showErrors={showLocalErrors}
								onValidate={handleValidation}
								onChange={handleInputChange}
								required={true}
							/>
						</div>
					</div>
				)}
				<div className="flex justify-between">
					<button
						data-cy="modal-bike-fleet-close"
						onClick={onClose}
						disabled={checkOutState === CheckOutStateEnum.LOADING}
						className="w-[200px] py-2 rounded-lg border border-primary text-center hover:bg-primary hover:text-white"
					>
						{t("global.cancel")}
					</button>
					<button
						disabled={isNotReadyForConfirm}
						data-cy="modal-bike-fleet-confirm"
						onClick={onConfirm}
						className="w-[200px] py-2 rounded-lg border border-primary text-center bg-primary text-white"
					>
						{t("global.confirm")}
					</button>
				</div>
			</Modal>
		</Portal>
	)
}
